/**
 * The audio the player plays, when the file itself cannot be seeked.
 *
 * `<audio>` does not seek a VBR MP3 accurately. It reports back exactly the
 * time you asked for and plays something else: measured on a 67-minute
 * audiobook (Chrome 141, blob: URL, the same path the player uses), clicking
 * a line at 05:00 played the audio from 04:45 — 14.5 seconds away — while
 * `currentTime` said 300.000. There is nothing to fix in that: the file's
 * seek table is a Xing TOC, one entry per 1 % of the recording, and inside a
 * 40-second bucket the position is guessed from the average bitrate. A
 * recording that is quiet in one place and loud in another breaks the guess.
 * Rewriting the TOC with ffmpeg changes nothing — it is still 100 entries.
 *
 * The cue times are not the problem: the parts start where we say they start,
 * to a millisecond, and every downloaded subtitle file is correct. Only what
 * the browser does with a click is wrong.
 *
 * So a chunked recording gets a rendition the browser CAN seek: the parts we
 * already cut, concatenated. They are CBR, so position is byte-linear, and
 * the same measurement puts a click within 0.08 s of its line. What the
 * player then plays is exactly what the engine heard, which is the right
 * thing to be listening to while checking a transcript.
 *
 * The one thing that does not survive the concatenation is arithmetic on
 * nominal lengths. Each part carries an `Info` frame and LAME's own padding —
 * about 75 ms of it — so a part cut to 900 s occupies 900.075 s of the
 * rendition, and by the 40th part that is three seconds of drift. `spans`
 * holds where each part really landed, and the map below is what the player
 * uses instead of assuming the two timelines are the same one.
 */

/** One part: where it begins in the recording, and where it begins in the
 * rendition the player is given. */
export interface PlaybackSpan {
  /** Seconds from the start of the original recording — what a cue is timed in */
  origin: number
  /** Seconds from the start of the playback rendition */
  playback: number
}

export interface PlaybackAudio {
  file: File
  /** Ascending by `origin`; always starts at { origin: 0, playback: 0 } */
  spans: PlaybackSpan[]
}

/**
 * Where each part lands in the concatenation.
 *
 * Byte arithmetic, not frame counting: the parts are constant-bitrate, so
 * bytes ARE time, and this holds whatever sample rate LAME picked for a low
 * bitrate. It also counts the `Info` frame and the encoder padding, which is
 * the whole reason the two timelines differ.
 */
export function spansFromParts(
  parts: { offset: number; size: number }[],
  bitrateBps: number,
): PlaybackSpan[] {
  const bytesPerSecond = bitrateBps / 8
  const spans: PlaybackSpan[] = []
  let bytes = 0
  for (const part of parts) {
    spans.push({ origin: part.offset, playback: bytes / bytesPerSecond })
    bytes += part.size
  }
  return spans
}

/** The part containing `value`, by whichever timeline `key` names. */
function spanAt(spans: PlaybackSpan[], key: 'origin' | 'playback', value: number): PlaybackSpan {
  let found = spans[0]
  for (const span of spans) {
    if (span[key] > value) break
    found = span
  }
  return found
}

/** Recording time → the time to give the audio element. */
export function toPlayback(spans: PlaybackSpan[], seconds: number): number {
  if (spans.length === 0) return seconds
  const span = spanAt(spans, 'origin', seconds)
  return Math.max(0, seconds - span.origin + span.playback)
}

/** …and back, for the highlight that follows what is playing. */
export function fromPlayback(spans: PlaybackSpan[], seconds: number): number {
  if (spans.length === 0) return seconds
  const span = spanAt(spans, 'playback', seconds)
  return Math.max(0, seconds - span.playback + span.origin)
}

/**
 * Whether the browser can be trusted to seek this file, or whether the player
 * needs a rendition built for it.
 *
 * Only MP3 is in question. WAV carries samples, and m4a/ogg carry an exact
 * table of where every frame sits — a browser seeks those to the sample, so a
 * mic recording or a video's extracted track gains nothing from a rendition
 * and would only lose quality to it.
 *
 * An MP3 answers for itself in its first frame: an `Info` tag means the
 * encoder declared a constant bitrate, and position is then byte-linear —
 * measured at 52 ms on an hour-long file, which is exact enough for anyone.
 * A `Xing` tag means variable, and that is the 14-second case. **No tag at
 * all is treated as variable too**: a headerless VBR file is the worst thing
 * a player can be handed, and guessing "constant" there would leave the bug
 * in place for the file least able to survive it. The cost of being wrong the
 * other way is one needless re-encode.
 */
export async function seekIsUnreliable(file: File): Promise<boolean> {
  if (!looksLikeMp3(file)) return false
  try {
    const start = id3Length(new Uint8Array(await file.slice(0, 10).arrayBuffer()))
    const frame = new Uint8Array(await file.slice(start, start + 1024).arrayBuffer())
    return !declaresConstantBitrate(frame)
  } catch {
    return true
  }
}

function looksLikeMp3(file: File): boolean {
  return /\.mp3$/i.test(file.name) || file.type === 'audio/mpeg' || file.type === 'audio/mp3'
}

/** Bytes before the first MPEG frame. An ID3v2 tag is a header plus a
 * syncsafe length — seven bits per byte, the eighth always clear — and it is
 * routinely large: the audiobook that started all this carries 73 KB of cover
 * art before a single sample. */
export function id3Length(head: Uint8Array): number {
  if (head.length < 10) return 0
  if (head[0] !== 0x49 || head[1] !== 0x44 || head[2] !== 0x33) return 0
  return 10 + ((head[6] << 21) | (head[7] << 14) | (head[8] << 7) | head[9])
}

/** Does the first frame carry the `Info` tag a constant-bitrate encoder
 * writes? `Xing` (variable) and a frame with neither both answer no. */
export function declaresConstantBitrate(bytes: Uint8Array): boolean {
  let sync = -1
  for (let i = 0; i < Math.min(bytes.length - 1, 64); i++) {
    if (bytes[i] === 0xff && (bytes[i + 1] & 0xe0) === 0xe0) {
      sync = i
      break
    }
  }
  if (sync < 0) return false
  // The tag sits at a fixed distance that depends on the frame's layout, so
  // the whole frame head is searched rather than one offset guessed
  const tag = bytes.subarray(sync, sync + 200)
  for (let i = 0; i + 4 <= tag.length; i++) {
    if (tag[i] === 0x49 && tag[i + 1] === 0x6e && tag[i + 2] === 0x66 && tag[i + 3] === 0x6f) return true // "Info"
    if (tag[i] === 0x58 && tag[i + 1] === 0x69 && tag[i + 2] === 0x6e && tag[i + 3] === 0x67) return false // "Xing"
  }
  return false
}
